import React, { useContext, useEffect, useState } from 'react';
import { Button, CircularProgress, FormControlLabel, Radio, RadioGroup } from '@material-ui/core';
import { Link } from 'react-router-dom';

import { doorDashApi } from 'api/doorDash';
import { goPuffApi } from 'api/goPuff';
import { UserContext } from 'contexts/UserContext';
import { DataTable, Notifications, WholesaleAccountPicker } from 'shared';
import dayjs from 'utils/dayjsConfig';

function ViewGoPuffBatches() {
    const [printableRows, setPrintableRows] = useState([]);
    const [printedRows, setPrintedRows] = useState([]);
    const [allocationRows, setAllocationRows] = useState([]);
    const [notification, setNotification] = useState({ text: null, level: null });
    const [printableRowsGroupBy, setPrintableRowsGroupBy] = useState('lineHaul');
    const [lineHaulGroupedRows, setLineHaulGroupedRows] = useState([]);
    const [gpMarketGroupedRows, setGpMarketGroupedRows] = useState([]);
    const user = useContext(UserContext);
    const { wholesaleAccount } = useContext(UserContext);
    const [api, setApi] = useState(wholesaleAccount.fbmAccountName === 'doordash' ? doorDashApi : goPuffApi);
    const [accountAbbr, setAccountAbbr] = useState(wholesaleAccount.fbmAccountName === 'doordash' ? 'DD' : 'GP');

    const getPrintableRows = async () => {
        const response = await api.getPrintableBatches(user.getFacilityID());
        if (response.status === false) {
            setNotification({ text: `No entries found ${response.msg}`, level: 'error' });
            setPrintableRows([]);
            return;
        }

        setPrintableRows(response.data.rows.lineHaulGroupedArr ? response.data.rows.lineHaulGroupedArr : []);
        setLineHaulGroupedRows(response.data.rows.lineHaulGroupedArr ? response.data.rows.lineHaulGroupedArr : []);
        setGpMarketGroupedRows(
            response.data.rows.gpPackMarketGroupedArr ? response.data.rows.gpPackMarketGroupedArr : [],
        );
    };

    const getPrintedRows = async () => {
        const response = await api.getPrintedBatches(user.getFacilityID());
        if (response.status === false) {
            setNotification({ text: `No entries found ${response.msg}`, level: 'error' });
            setPrintableRows([]);
            return;
        }

        const rows = (response.data?.rows ?? []).map((row) => ({
            ...row,
            createdAt: dayjs(row.createdAt.date).format('MM/DD/YYYY HH:mm:ss'),
        }));

        setPrintedRows(rows);
    };

    const getAllocationRows = async () => {
        const response = await api.getAllocationRollup(user.getFacilityID());
        if (response.status === false) {
            setNotification({ text: `No entries found ${response.msg}`, level: 'error' });
            setPrintableRows([]);
            return;
        }

        const rows = (response.data?.rows ?? []).map((row) => ({
            ...row,
            deliveryDate: dayjs(row.deliveryDate.date).format('MM/DD/YYYY'),
        }));

        setAllocationRows(rows);
    };

    useEffect(() => {
        if (wholesaleAccount.fbmAccountName === 'doordash') {
            setApi(doorDashApi);
            setAccountAbbr('DD');
        } else {
            setApi(goPuffApi);
            setAccountAbbr('GP');
        }
    }, [wholesaleAccount]);

    useEffect(() => {
        getPrintableRows();
        getPrintedRows();
        getAllocationRows();
    }, [api]);

    const togglePrintableRows = (e) => {
        setPrintableRowsGroupBy(e.target.value);
        if (e.target.value === 'gpMarket') {
            setPrintableRows(gpMarketGroupedRows);
        } else {
            setPrintableRows(lineHaulGroupedRows);
        }
    };

    const allocationColumns = [
        { accessor: 'packingFC', Header: 'Packing FC' },
        { accessor: 'deliveryDate', Header: 'Delivery Date' },
        { accessor: 'numShipments', Header: 'Num Shipments' },
        {
            accessor: 'download',
            Header: 'Download Template',
            Cell: ({ cell: { value: initialValue }, column, editable, row, saveEdit }) => (
                <Button
                    id="Download"
                    onClick={async () => {
                        api.downloadAllocationCsv(user.getFacilityID(), row.original.deliveryDate);
                    }}
                >
                    Download Template
                </Button>
            ),
        },
    ];

    const printableColumns = [
        { accessor: 'lineHaulName', Header: 'Line Haul', hide: printableRowsGroupBy === 'gpMarket' },
        { accessor: 'gpPackMarket', Header: `${accountAbbr} Pack Market`, hide: printableRowsGroupBy === 'lineHaul' },
        { accessor: 'numMFCs', Header: 'Num MFCs' },
        { accessor: 'deliveryDate', Header: 'Delivery Date' },
        {
            accessor: 'print',
            Header: 'Print',
            Cell: ({ cell: { value: initialValue }, column, editable, row, saveEdit }) => {
                const [isLoading, setIsLoading] = useState(false);
                return (
                    <Button
                        id="Print"
                        disabled={isLoading}
                        onClick={async (e) => {
                            e.stopPropagation();
                            setIsLoading(true);
                            const value =
                                printableRowsGroupBy === 'gpMarket' ? row.original.gpPackMarket : row.original.id;

                            await api.printBatch(
                                user.getFacilityID(),
                                printableRowsGroupBy,
                                value,
                                row.original.deliveryDate,
                            );
                            getPrintableRows();
                            setIsLoading(false);
                        }}
                    >
                        {isLoading ? <CircularProgress size={24} /> : 'Print'}
                    </Button>
                );
            },
        },
    ];

    const printedColumns = [
        { accessor: 'batchID', Header: 'Batch ID' },
        { accessor: 'createdAt', Header: 'Printed On' },
        { accessor: 'numItems', Header: 'Num Shipments' },
        { accessor: 'creator', Header: 'Printed By' },
        {
            accessor: 'download',
            Header: 'Download',
            Cell: ({ cell: { value: initialValue }, column, editable, row, saveEdit }) => {
                const [isLoading, setIsLoading] = useState(false);
                return (
                    <Button
                        id="Download"
                        disabled={isLoading}
                        onClick={async () => {
                            setIsLoading(true);
                            await api.reprintBatch(row.original.batchID);
                            setIsLoading(false);
                        }}
                    >
                        {isLoading ? <CircularProgress size={24} /> : 'Download PDF'}
                    </Button>
                );
            },
        },
    ];

    return (
        <div>
            <WholesaleAccountPicker />
            {!!wholesaleAccount.id && (
                <>
                    <h1>{wholesaleAccount.companyName} Batches</h1>
                    <Notifications options={notification} />
                    <div>
                        <h2>Available for Allocation Adjustment</h2>
                        <Link to="/gopuff/adjustments-upload">Upload Adjustments</Link>
                        <DataTable columns={allocationColumns} data={allocationRows} />
                        <h2>Available to Print</h2>
                        <div>
                            <RadioGroup
                                style={{ width: 'auto' }}
                                defaultValue="lineHaul"
                                value={printableRowsGroupBy}
                                onChange={togglePrintableRows}
                                row
                            >
                                <FormControlLabel value="lineHaul" control={<Radio />} label="Group By Line Haul" />
                                <FormControlLabel
                                    value="gpMarket"
                                    control={<Radio />}
                                    label={`Group By ${accountAbbr} Pack Market`}
                                />
                            </RadioGroup>
                        </div>
                        <DataTable
                            columns={printableColumns}
                            data={printableRows}
                            expandable
                            ExpansionComponent={
                                <ViewLineItems
                                    setNotification={setNotification}
                                    printableRowsGroupBy={printableRowsGroupBy}
                                    wholesaleAccount={wholesaleAccount}
                                    api={api}
                                />
                            }
                        />
                        <h2>Printed</h2>
                        <DataTable columns={printedColumns} data={printedRows} />
                    </div>
                </>
            )}
        </div>
    );
}

function ViewLineItems({ api, parentRow, printableRowsGroupBy, setNotification, wholesaleAccount }) {
    const [lineData, setLineData] = useState([]);
    const [searchVal, setSearchVal] = useState(
        printableRowsGroupBy === 'lineHaul' ? parentRow.original.id : parentRow.original.gpPackMarket,
    );
    const user = useContext(UserContext);

    const getLineData = async () => {
        const res = await api.getPrintableBatchesDetails(
            printableRowsGroupBy,
            searchVal,
            parentRow.original.deliveryDate,
        );
        if (res.status === false) {
            setNotification({ text: `No printable batches Micro FC shipments found ${res.msg}`, level: 'error' });
            setLineData([]);
            return;
        }

        setLineData(res.data.rows ? res.data.rows : []);
    };

    useEffect(() => {
        if (wholesaleAccount.id) {
            getLineData();
        }
    }, [wholesaleAccount]);
    const columns = [
        { accessor: 'microFC', Header: 'Micro FC' },
        { accessor: 'poNum', Header: 'PO ID' },
        { accessor: 'numShipments', Header: 'Num Shipments' },
        {
            accessor: 'print',
            Header: 'Print',
            Cell: ({ cell: { value: initialValue }, row }) => (
                <Button
                    id="Print"
                    onClick={async () => {
                        await api.printBatch(
                            user.getFacilityID(),
                            'microFC',
                            row.original.microFC,
                            parentRow.original.deliveryDate,
                        );

                        getLineData();
                    }}
                >
                    Print
                </Button>
            ),
        },
    ];

    return <DataTable columns={columns} data={lineData} />;
}

export { ViewGoPuffBatches };
